/**
 * Banques d'images émotionnelles (review film) + alias de clés.
 * Fichiers servis depuis public/emotions/ (préfixe BASE_URL Vite).
 */

/** Couleur de bordure emprunt (hors T/SD/D) */
export const BORROWED_BORDER_COLOR = '#f59e0b'

/** Suffixes de casting (renversement + emprunt) : même pool d'images, clé distincte. */
export const CAST_INVERSION_SUFFIXES = new Set(['6', '64', '65', '43', '2'])
export const CAST_BORROWED_SUFFIX = 'b'
export const CAST_POOL_SUFFIXES = new Set([...CAST_INVERSION_SUFFIXES, CAST_BORROWED_SUFFIX])

/**
 * Retire les suffixes de casting d'une clé (ex. D:6:b → D, I:6 → I).
 * @param {string} emotionKey
 * @returns {string}
 */
export function stripCastSuffix(emotionKey) {
  if (!emotionKey || emotionKey.startsWith('function:') || emotionKey.startsWith('cadence:')) {
    return emotionKey
  }
  let key = emotionKey
  let changed = true
  while (changed) {
    changed = false
    const idx = key.lastIndexOf(':')
    if (idx <= 0) break
    const suffix = key.slice(idx + 1)
    if (CAST_POOL_SUFFIXES.has(suffix)) {
      key = key.slice(0, idx)
      changed = true
    }
  }
  return key
}

/**
 * Catalog par clé banque : variants = noms de fichiers sous public/emotions/
 * Clés majuscules = majeur, minuscules = mineur (sauf pools unifiés).
 */
export const EMOTION_BANKS = {
  CP: {
    label: 'Entrée / conclusion',
    borderFunction: 'T',
    variants: [
      'CP (2).png',
      'CP (4).png',
      'CP (6).png',
      'CP (8).png',
      'CP (9).png',
      'CP ou cp (3).png',
    ],
  },
  cp: {
    label: 'Entrée / conclusion',
    borderFunction: 'T',
    variants: ['cp.png', 'cp (10).png', 'CP ou cp (3).png'],
  },
  CR: {
    label: 'Surprise',
    borderFunction: 'T',
    variants: ['CR.png', 'CR (2).png', 'CR (3).png', 'CR (4).png', 'CR1.png'],
  },
  plagal: {
    label: 'Retour doux',
    borderFunction: 'SD',
    variants: ['plagal1.png', 'plagal2.png'],
  },
  demi: {
    label: 'Suspens / question',
    borderFunction: 'D',
    variants: ['demi.png', 'demi (2).png', 'demi (3).png', 'demi (4).png'],
  },
  CAD64: {
    label: 'Suspension cadentielle',
    borderFunction: 'D',
    variants: ['CAD64-1.png', 'CAD64-2.png', 'CAD64-6.png'],
  },
  cad64: {
    label: 'Suspension cadentielle',
    borderFunction: 'D',
    variants: ['cad64-3.png', 'cad64-4.png', 'cad64-5.png'],
  },
  '64passage': {
    label: 'Passage / suspension',
    borderFunction: 'D',
    variants: ['64passage.png', '64passage2.png', '64passage3.png'],
  },
  I: {
    label: 'Stabilité',
    borderFunction: 'T',
    variants: [
      'I (2).png',
      'I (3).png',
      'I (4).png',
      'I (6).png',
      'I (7).png',
      'I (8).png',
      'I (11).png',
      'I (12).png',
    ],
  },
  i: {
    label: 'Repos intérieur',
    borderFunction: 'T',
    variants: [
      'i.png',
      'i1.png',
      'i (5).png',
      'i (9).png',
      'i (10).png',
      'i (13).png',
      'i (14).png',
      'i (15).png',
      'i (16).png',
      'i (17).png',
      'i (18).png',
    ],
  },
  SD: {
    label: 'Préparation / ouverture',
    borderFunction: 'SD',
    variants: [
      'SD.png',
      'SD (2).png',
      'SD (5).png',
      'SD (6).png',
      'SD (7).png',
      'SD (8).png',
      'SD (10).png',
      'SD (11).png',
      'SD (12).png',
      'SD (13).png',
      'SD (14).png',
      'SD (20).png',
      'SD (21).png',
    ],
  },
  sd: {
    label: 'Préparation intérieure',
    borderFunction: 'SD',
    variants: [
      'sd1.png',
      'sd (3).png',
      'sd (4).png',
      'sd (9).png',
      'sd (15).png',
      'sd (16).png',
      'sd (17).png',
      'sd (18).png',
      'sd (19).png',
    ],
  },
  D: {
    label: 'Tension / appel',
    borderFunction: 'D',
    variants: [
      'D.png',
      'D (2).png',
      'D2.png',
      'D3.png',
      'D4.png',
      'D5.png',
      'D6.png',
      'D7.png',
      'D8.png',
    ],
  },
  III: {
    label: 'Parallèle',
    borderFunction: 'T',
    variants: ['III (2).png'],
  },
  iii: {
    label: 'Parallèle',
    borderFunction: 'T',
    variants: ['iii.png', 'iii1.png', 'iii (3).png'],
  },
  /** Visages dédiés au vi (union avec `i` via alias pour le 6e degré en majeur). */
  viFaces: {
    label: 'Parallèle',
    borderFunction: 'T',
    variants: ['vi.png', 'vi (2).png', 'vi (3).png', 'vi1.png'],
  },
  /** VI en mineur : sous-ensemble I/i plus doux (pas le pool I complet). */
  VI: {
    label: 'Stabilité',
    borderFunction: 'T',
    variants: ['I (2).png', 'I (4).png', 'i (5).png', 'I (8).png', 'i (10).png'],
  },
  vii7: {
    label: 'Tension (vii7)',
    borderFunction: 'D',
    variants: ['vii7.png', 'vii7 (2).png'],
  },
  Pedal: {
    label: 'Pédale / couleur',
    borderFunction: 'SD',
    variants: ['Pedal.png', 'Pedal (2).png', 'Pedal (3).png'],
  },
}

/** Alias logique → clé banque (ou liste pour union) */
export const EMOTION_ALIASES = {
  'cadence:evitee': 'CR',
  'cadence:rompue': 'CR',
  'cadence:plagal': 'plagal',
  'cadence:demi-cadence': 'demi',
  'cadence:imperfect:major': 'I',
  'cadence:imperfect:minor': 'i',
  'cadence:perfect:major': 'CP',
  'cadence:perfect:minor': 'cp',
  'I64-tonique': '64passage',
  'V64-passage': '64passage',
  /** 6e degré en majeur (accord mineur) : tous les i + visages vi */
  vi: ['i', 'viFaces'],
  II: 'SD',
  ii: 'sd',
  N6: 'SD',
  d: 'sd',
  VII: 'D',
  It: 'Pedal',
  Fr: 'Pedal',
  Gr: 'Pedal',
  Pedal: 'Pedal',
}

/**
 * Banques qui alimentent le pool union d'une fonction (mode fonctions seules).
 * @param {'T'|'SD'|'D'} fn
 * @param {boolean} isMinor
 */
export function getFunctionUnionBankKeys(fn, isMinor) {
  if (fn === 'T') {
    return isMinor ? ['i', 'iii', 'viFaces'] : ['I', 'III']
  }
  if (fn === 'SD') {
    return isMinor ? ['sd'] : ['SD']
  }
  if (fn === 'D') {
    // Pas de banque `d` dédiée : tension = pool D + vii7 (sd reste pour le degré d via alias)
    return ['D', 'vii7']
  }
  return []
}

/**
 * Fusionne plusieurs banques en un pool (dédoublonnage).
 * @param {string[]} bankKeys
 * @returns {{ label: string, borderFunction: string|null, variants: string[] }}
 */
function mergeEmotionBanks(bankKeys) {
  const variants = []
  const seen = new Set()
  let label = 'Neutre'
  let borderFunction = null
  for (const bk of bankKeys) {
    const bank = EMOTION_BANKS[bk]
    if (!bank) continue
    label = bank.label
    borderFunction = bank.borderFunction
    for (const v of bank.variants) {
      if (!seen.has(v)) {
        seen.add(v)
        variants.push(v)
      }
    }
  }
  return { label, borderFunction, variants }
}

export function emotionAssetUrl(fileName) {
  if (!fileName) return null
  const base = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL)
    ? import.meta.env.BASE_URL
    : '/Opus-Lab/'
  const root = String(base).endsWith('/') ? String(base) : `${base}/`
  const rel = `${root}emotions/${fileName.split('/').map(encodeURIComponent).join('/')}`
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      return new URL(rel, window.location.origin).href
    } catch (_) {
      return rel
    }
  }
  return rel
}

/**
 * Résout les variants pour une clé banque (après alias).
 * @param {string} bankKey
 * @returns {{ label: string, borderFunction: string, variants: string[] } | null}
 */
export function getEmotionBank(bankKey) {
  if (!bankKey) return null
  const aliased = EMOTION_ALIASES[bankKey] !== undefined ? EMOTION_ALIASES[bankKey] : bankKey
  if (Array.isArray(aliased)) {
    const merged = mergeEmotionBanks(aliased)
    if (merged.variants.length === 0) return null
    return merged
  }
  return EMOTION_BANKS[aliased] || null
}

/**
 * Construit le pool de fichiers pour une clé émotionnelle (y compris unions function:).
 * @param {string} emotionKey
 * @returns {{ label: string, borderFunction: string|'borrowed', variants: string[], bankKey: string }}
 */
export function getVariantsForEmotionKey(emotionKey) {
  if (!emotionKey) {
    return { label: 'Neutre', borderFunction: null, variants: [], bankKey: 'neutral' }
  }

  if (emotionKey.startsWith('function:')) {
    const parts = emotionKey.split(':') // function:T:major | function:T:minor
    const fn = parts[1]
    const isMinor = parts[2] === 'minor'
    const bankKeys = getFunctionUnionBankKeys(fn, isMinor)
    const merged = mergeEmotionBanks(bankKeys)
    return {
      label: merged.label || fn,
      borderFunction: merged.borderFunction || fn,
      variants: merged.variants,
      bankKey: emotionKey,
    }
  }

  const bankKey = stripCastSuffix(emotionKey)
  const aliased = EMOTION_ALIASES[bankKey] !== undefined ? EMOTION_ALIASES[bankKey] : bankKey
  if (Array.isArray(aliased)) {
    const merged = mergeEmotionBanks(aliased)
    if (merged.variants.length === 0) {
      return { label: 'Neutre', borderFunction: null, variants: [], bankKey: 'neutral' }
    }
    return {
      label: merged.label,
      borderFunction: merged.borderFunction,
      variants: merged.variants,
      bankKey,
    }
  }
  const bank = EMOTION_BANKS[aliased]
  if (!bank) {
    return { label: 'Neutre', borderFunction: null, variants: [], bankKey: 'neutral' }
  }
  return {
    label: bank.label,
    borderFunction: bank.borderFunction,
    variants: [...bank.variants],
    bankKey: aliased,
  }
}
